import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import axiosClient from "../../api/axios";
import LinkUpload from "../../api/LinkUpload";
import { Spin, Modal } from "antd";

import './MemberView.css';

// ================= COMPONENT PHÂN TRANG =================
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 1) {
        return null;
    }

    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
            window.scrollTo(0, 0);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const startPage = Math.max(1, currentPage - 1);
        const endPage = Math.min(totalPages, currentPage + 1);

        if (startPage > 1) {
            pageNumbers.push(<li key={1}><a className="page-numbers" onClick={() => handlePageClick(1)}>1</a></li>);
            if (startPage > 2) {
                pageNumbers.push(<li key="dots1"><span className="page-numbers dots">…</span></li>);
            }
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <li key={i}>
                    {i === currentPage ? (
                        <span className="page-numbers current">{i}</span>
                    ) : (
                        <a className="page-numbers" onClick={() => handlePageClick(i)}>{i}</a>
                    )}
                </li>
            );
        }
        if (endPage < totalPages) {
             if (endPage < totalPages - 1) {
                pageNumbers.push(<li key="dots2"><span className="page-numbers dots">…</span></li>);
            }
            pageNumbers.push(<li key={totalPages}><a className="page-numbers" onClick={() => handlePageClick(totalPages)}>{totalPages}</a></li>);
        }
        return pageNumbers;
    };

    return (
        <nav className="pagination">
            <ul>
                {currentPage > 1 && (<li><a className="prev page-numbers" onClick={() => handlePageClick(currentPage - 1)}>«</a></li>)}
                {renderPageNumbers()}
                {currentPage < totalPages && (<li><a className="next page-numbers" onClick={() => handlePageClick(currentPage + 1)}>»</a></li>)}
            </ul>
        </nav>
    );
};
// ============================================================


const MemberView = () => {
    const token = localStorage.getItem('token');
    const header = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    };

    const [allMembers, setAllMembers] = useState([]); // Lưu toàn bộ danh sách từ API
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    
    // State cho phân trang
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    // State cho Modal Giới thiệu
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    const showIntroModal = (member) => {
        setSelectedMember(member);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedMember(null);
    };

    // Load dữ liệu một lần duy nhất khi component mount
    useEffect(() => {
        const LoadDataMembers = () => {
            setLoading(true);
            // Gửi filter rỗng để lấy tất cả thành viên
            axiosClient.post('/Members/GetMembersAll', {}, { headers:header })
                .then(res => {
                    const sortedData = res.data.sort((a, b) => new Date(a.JoinDate) - new Date(b.JoinDate));
                    setAllMembers(sortedData);
                })
                .catch(err => {
                    console.error("Lỗi khi tải dữ liệu:", err);
                    setAllMembers([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        };
        window.scrollTo(0, 0);
        LoadDataMembers();
    }, []); // Dependency rỗng đảm bảo chỉ chạy 1 lần

    // Logic tìm kiếm client-side bằng useMemo
    const filteredMembers = useMemo(() => {
        // Nếu không có từ khóa, trả về toàn bộ danh sách
        if (!searchTerm.trim()) {
            return allMembers;
        }
        
        const lowercasedFilter = searchTerm.toLowerCase().trim();
        
        return allMembers.filter(member => {
            // Kiểm tra khớp với tên hoặc số thẻ (đảm bảo an toàn)
            const nameMatch = member.FullName?.toLowerCase().includes(lowercasedFilter);
            const codeMatch = member.Code?.toLowerCase().includes(lowercasedFilter);
            return nameMatch || codeMatch;
        });
    }, [searchTerm, allMembers]);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setCurrentPage(1); // Reset về trang 1 mỗi khi tìm kiếm
        // Việc lọc đã được useMemo xử lý tự động
    };

    // Phân trang trên dữ liệu đã được lọc
    const currentMembersOnPage = useMemo(() => {
        const firstItemIndex = (currentPage - 1) * itemsPerPage;
        const lastItemIndex = firstItemIndex + itemsPerPage;
        return filteredMembers.slice(firstItemIndex, lastItemIndex);
    }, [currentPage, filteredMembers]);

    return (
        <div className="container main-body">
            <main>
                <div className="directory-controls">
                    <form className="search-bar" onSubmit={handleSearchSubmit}>
                        <input 
                            type="text" 
                            placeholder="Tìm luật sư theo tên hoặc số thẻ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button type="submit" aria-label="Tìm kiếm">
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </button>
                    </form>
                </div>

                <section>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
                    ) : filteredMembers.length === 0 ? ( // Kiểm tra trên danh sách đã lọc
                        <div className="no-results" style={{ display: 'block' }}>
                            <i className="fa-solid fa-folder-open"></i>
                            <h4>Không tìm thấy kết quả</h4>
                            <p>Không có luật sư nào phù hợp với tiêu chí tìm kiếm của bạn.</p>
                        </div>
                    ) : (
                        <div className="lawyer-grid">
                            {currentMembersOnPage.map(item => { // Render danh sách cho trang hiện tại
                                const CardWrapper = item.Link ? 'a' : 'div';
                                const cardProps = item.Link 
                                    ? { href: item.Link, target: '_blank', rel: 'noopener noreferrer' } 
                                    : {};

                                return (
                                    <CardWrapper {...cardProps} className="lawyer-card" key={item.ID}>
                                        <img 
                                            src={item.Image ? LinkUpload(item.Image) : "https://placehold.co/300x400/EFEFEF/333?text=Ảnh+3x4"} 
                                            alt={`Ảnh luật sư ${item.FullName}`} 
                                            className="lawyer-card__photo"
                                        />
                                        <div className="lawyer-card__content">
                                            <h3 className="lawyer-card__name">{item.FullName}</h3>
                                            <div className="lawyer-card__info">
                                                <p><i className="fa-solid fa-id-card"></i><span className="info-label">Số thẻ:</span><span>{item.Code || 'N/A'}</span></p>
                                                <p><i className="fa-solid fa-cake-candles"></i><span className="info-label">Ngày sinh:</span><span>{item.BirthDay ? moment(item.BirthDay).format('DD/MM/YYYY') : 'N/A'}</span></p>
                                                <p><i className="fa-solid fa-calendar-check"></i><span className="info-label">Gia nhập:</span><span>{item.JoinDate ? moment(item.JoinDate).format('DD/MM/YYYY') : 'N/A'}</span></p>
                                                <p className="tchn-row"><i className="fa-solid fa-building"></i>
                                                    <span>{item.Organization || 'Chưa cập nhật'}</span>
                                                </p>
                                            </div>
                                            
                                            {item.Introduction && (
                                                <div className="lawyer-card__actions">
                                                    <button 
                                                        className="view-intro-btn" 
                                                        onClick={(e) => {
                                                            if (item.Link) e.preventDefault(); 
                                                            showIntroModal(item);
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-circle-info"></i>
                                                        Xem giới thiệu
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </CardWrapper>
                                );
                            })}
                        </div>
                    )}
                </section>
        
                {!loading && filteredMembers.length > 0 && ( // Kiểm tra trên danh sách đã lọc
                     <Pagination
                        totalItems={filteredMembers.length} // Phân trang trên danh sách đã lọc
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                )}
            </main>

            {selectedMember && (
                <Modal
                    title="Giới thiệu Luật sư"
                    open={isModalVisible}
                    onCancel={handleModalClose}
                    footer={null}
                    width={600}
                    className="intro-modal"
                >
                    <img
                        src={selectedMember.Image ? LinkUpload(selectedMember.Image) : "https://placehold.co/300x400/EFEFEF/333?text=Ảnh+3x4"}
                        alt={`Ảnh luật sư ${selectedMember.FullName}`}
                        className="intro-modal-avatar"
                    />
                    <h2 className="intro-modal-name">{selectedMember.FullName}</h2>
                    <p className="intro-modal-content">
                        {selectedMember.Introduction}
                    </p>
                </Modal>
            )}
        </div>
    );
};

export default MemberView;